import { useEffect, useState, type ReactNode } from 'react'
import { NavLink } from 'react-router'
import { CalendarDays, ClipboardCheck, Home, LayoutGrid, MonitorPlay, ScanQrCode, UserRound } from 'lucide-react'
import { getMode, subscribeMode, type ApiMode } from '../lib/api'

interface MastheadProps {
  /** Small uppercase line above the title — date, day number, section. */
  kicker?: ReactNode
  title: ReactNode
  sub?: ReactNode
  /** Right-aligned control on the title's baseline. */
  action?: ReactNode
  rule?: boolean
}

export function Masthead({ kicker, title, sub, action, rule = true }: MastheadProps) {
  return (
    <header className="masthead">
      {kicker ? <p className="kicker">{kicker}</p> : null}
      <div className="masthead__row">
        <h1 className="display masthead__title">{title}</h1>
        {action}
      </div>
      {sub ? <p className="masthead__sub">{sub}</p> : null}
      {rule ? <hr className="rule-thick masthead__rule" /> : null}
    </header>
  )
}

const ICON = { size: 21, strokeWidth: 1.4 } as const

interface TabItem {
  to: string
  label: string
  icon: ReactNode
  badge?: number
  /**
   * Exact-match this path. Needed wherever one tab's path is a prefix of another's
   * ("/venue" vs "/venue/settings"), which would otherwise light both at once.
   * Left false for tabs with child routes that should keep the tab active, such as
   * "/scan" while on "/scan/:slotId".
   */
  end?: boolean
}

export function TabBar({ items }: { items: TabItem[] }) {
  return (
    <nav className="tabbar" aria-label="Primary">
      {items.map((item) => (
        <NavLink key={item.to} to={item.to} className="tabbar__item" end={item.end ?? true}>
          <span className="tabbar__mark" aria-hidden="true" />
          <span className="tabbar__icon">
            {item.icon}
            {item.badge ? (
              <span className="tabbar__badge tnum" aria-hidden="true">
                {item.badge > 99 ? '99+' : item.badge}
              </span>
            ) : null}
          </span>
          {item.label}
        </NavLink>
      ))}
    </nav>
  )
}

export function participantTabs(_checkedIn?: number): TabItem[] {
  return [
    { to: '/', label: 'Home', icon: <Home {...ICON} /> },
    { to: '/programme', label: 'Programme', icon: <CalendarDays {...ICON} /> },
    // Stays lit while the scanner is open at /scan/:slotId.
    { to: '/attendance', label: 'Attendance', icon: <ClipboardCheck {...ICON} />, end: true },
    { to: '/account', label: 'Account', icon: <UserRound {...ICON} /> },
  ]
}

/*
 * A function of the tier now, not a constant.
 *
 * A `qr_display` account runs a projector and scans passes; it has no business holding
 * the certificate toggle, the room roster or a report carrying every delegate's email.
 * Three tabs for a steward, six for an organiser - and the route tree in App.tsx does
 * not mount the other three either, so this is presentation over a real boundary rather
 * than instead of one.
 */
export function venueTabs(admin: boolean, _superAdmin = false): TabItem[] {
  return [
    { to: '/venue', label: 'Display', icon: <MonitorPlay {...ICON} /> },
    { to: '/staff/pass', label: 'Passes', icon: <ScanQrCode {...ICON} /> },
    /*
     * One tab for every organiser tool. The bar had reached seven at 375px and each
     * new screen was a choice between an eighth tab and a link at the foot of
     * Settings; /admin is the hub they all sit behind now, super admin's Users too.
     */
    ...(admin ? [
      { to: '/admin', label: 'Manage', icon: <LayoutGrid {...ICON} /> },
    ] : []),
    { to: '/account', label: 'Account', icon: <UserRound {...ICON} /> },
  ]
}

/**
 * States plainly when the screen is showing fixtures rather than live data.
 * Silence here would be worse than the banner: a demo that looks like production
 * is how wrong numbers get believed.
 */
export function ApiModeBanner() {
  const [mode, setMode] = useState<ApiMode>(getMode)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => subscribeMode(setMode), [])
  useEffect(() => {
    if (typeof window !== 'undefined') setDismissed(window.sessionStorage.getItem('cpd-demo-banner-dismissed') === '1')
  }, [])

  // Only expose the developer/demo notice in development or when explicitly enabled.
  const explicitlyEnabled = import.meta.env.VITE_SHOW_SAMPLE_BANNER === '1'
  if (mode !== 'mock' || dismissed || (!import.meta.env.DEV && !explicitlyEnabled)) return null

  return (
    <div className="modebanner" role="status">
      <span className="modebanner__dot" aria-hidden="true" />
      <span><strong>Demo data</strong> — this screen is using local sample data.</span>
      <button
        type="button"
        className="modebanner__close"
        aria-label="Dismiss demo data notice"
        onClick={() => {
          window.sessionStorage.setItem('cpd-demo-banner-dismissed', '1')
          setDismissed(true)
        }}
      >×</button>
    </div>
  )
}
